import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import axios from "axios";
import { createRoot } from "react-dom/client";
import ReactDOM from "react-dom";
import styled from "styled-components";
import { animated, useSpring } from "react-spring";
import * as Babel from "@babel/standalone";
import { v4 as uuidv4 } from "uuid";
import { useHistory, useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import "./AreaOfCircle.css";
import tester from "./geoTest.json";
import Editor from "react-simple-code-editor";
import { highlight, languages } from "prismjs/components/prism-core";
import "prismjs/components/prism-clike";
import "prismjs/components/prism-javascript";
import "prismjs/themes/prism.css";
import { MathJax, MathJaxContext } from "better-react-mathjax";
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
  Line,
} from "react-simple-maps";
import { scaleQuantile } from "d3-scale";
import { motion, AnimatePresence } from "framer-motion";
import { Tooltip } from "react-tooltip";
import * as LucideIcons from "lucide-react";
import * as Recharts from "recharts";
import html2Canvas from "html2canvas";
import * as THREE from "three";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button as ShadButton } from "../ui/button";
import { Slider } from "../ui/slider";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../ui/accordian";
import { Alert, AlertDescription, AlertTitle } from "../ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertDialogPortal,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../ui/alertDialog";
import { AspectRatio } from "../ui/aspectRatio";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import {
  Breadcrumb,
  BreadcrumbEllipsis,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "../ui/breadcrumb";
import { Calendar } from "../ui/calender";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "../ui/carousal";
import { Checkbox } from "../ui/checkbox";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../ui/collapsible";
import { ComboboxDemo } from "../ui/combobox";
import {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from "../ui/command";
import {
  ContextMenu,
  ContextMenuCheckboxItem,
  ContextMenuContent,
  ContextMenuGroup,
  ContextMenuItem,
  ContextMenuLabel,
  ContextMenuPortal,
  ContextMenuRadioGroup,
  ContextMenuRadioItem,
  ContextMenuSeparator,
  ContextMenuShortcut,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
  ContextMenuTrigger,
} from "../ui/contextmenu";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerPortal,
  DrawerTitle,
  DrawerTrigger,
} from "../ui/drawer";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "../ui/dropdownmenu";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  useFormField,
} from "../ui/form";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "../ui/hovercard";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSeparator,
  InputOTPSlot,
} from "../ui/inputotp";
import {
  Menubar,
  MenubarCheckboxItem,
  MenubarContent,
  MenubarGroup,
  MenubarItem,
  MenubarLabel,
  MenubarMenu,
  MenubarPortal,
  MenubarRadioGroup,
  MenubarRadioItem,
  MenubarSeparator,
  MenubarShortcut,
  MenubarSub,
  MenubarSubContent,
  MenubarSubTrigger,
  MenubarTrigger,
} from "../ui/menubar";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "../ui/pagination";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Progress } from "../ui/progress";
import { RadioGroup, RadioGroupItem } from "../ui/radiogroup";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "../ui/resizable";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Separator } from "../ui/seperator";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetOverlay,
  SheetPortal,
  SheetTitle,
  SheetTrigger,
} from "../ui/sheet";
import { Skeleton } from "../ui/skeleton";
import { Toaster } from "../ui/sonner";
import { Switch as ShadSwitch } from "../ui/switch";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Textarea } from "../ui/textarea";
import {
  Toast,
  ToastAction,
  ToastClose,
  ToastDescription,
  ToastProvider,
  ToastTitle,
  ToastViewport,
} from "../ui/toast";
import { Toggle, toggleVariants } from "../ui/toggle";
import { ToggleGroup, ToggleGroupItem } from "../ui/togglegroup";
import { TooltipContent, TooltipProvider, TooltipTrigger } from "../ui/tooltip";
import {
  Button,
  CircularProgress,
  Grid,
  Switch,
  TextField,
  Typography,
  Popover as MuiPopover,
  Menu,
  MenuItem,
  Box,
  Stack,
} from "@mui/material";
import { ArrowDropDown, Lock } from "@mui/icons-material";
import p5 from "p5";
import { profJimBrandColors } from "../../theme/variants";
import Add from "@mui/icons-material/Add";
import {
  promptEngineeringPlaygroundClientId,
  promptEngineeringPlaygroundColorConfig,
} from "./themeConfigs";
import Moveable, { ElementGuidelineValueOption } from "react-moveable";
import CodePreviewPanel from "./CodePreviewPanel";
import {
  systemPromptToGenerateInteractives,
  multiStagePrompts,
  promptPlaceholders,
  GameType,
  gameTypeLabels,
  gameTypeRequirements,
  simulationSystemPrompt,
} from "./prompts";
import anthropic, { streamAnthropicMessages } from "./apiService";
import { generateGamePayload } from "../../utils/utils";
import { injectExternalCss } from "../../utils/inject";
import { analytics } from "../../utils/analytics";
import PromptService, { AI_MODELS } from "./promptsService";
import { templateManager } from "./templates/templateManager";
import confetti from "canvas-confetti";
import DOMPurify from "dompurify";
import { DEFAULT_BACKGROUND_IMAGE_URL } from "../../constants";
import MathText from "../common/MathText";

export const activeTabValue = {
  preview: 1,
  code: 2,
};

export const mathJaxConfig = {
  loader: { load: ["input/asciimath"] },
  asciimath: {
    displaystyle: true,
    delimiters: [
      ["``", "``"],
      ["```", "```"],
    ],
  },
};

export const GenerationMode = {
  fast: "fast",
  quality: "quality",
};

export const replacePromptPlaceholders = (prompt, { colors, fonts }) => {
  return prompt
    .replace(`{{COLORSCHEME}}`, colors)
    .replace(`{{FONTFAMILY}}`, fonts);
};

export const getGameUrl = (baseUrl, gameId, gameType) => {
  const typeParam = gameType ? `?type=${gameType}` : "";
  return `${baseUrl}/interactive-viewer/${gameId}${typeParam}`;
};

const trackEvent = (event, traceId, data) => {
  // analytics.trackEvent(event, {
  //   eventProperties: data,
  //   traceId,
  // });
};

const fetchInteractiveDataFromBlob = async (gameId) => {
  try {
    const response = await fetch(
      `https://devprofjim.blob.core.windows.net/public-assets/${gameId}.json`
    );
    if (!response.ok) {
      throw new Error("Failed to fetch game data");
    }
    const data = await response.json();
    return data;
  } catch (error) {
    throw error;
  }
};

const CodeEditorAndRunner = () => {
  const [inputCode, setInputCode] = useState("");
  const [generatedJSX, setGeneratedJSX] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [output, setOutput] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [generatedComponent, setGeneratedComponent] = useState(null);
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const isEmbed = urlParams.get("isEmbed");
  const codeFromUrl = urlParams.get("code");
  const isMultiStage = urlParams.get("isMultiStage");
  const clientType = urlParams.get("client");
  const instructions =
    urlParams.get("instruction") || urlParams.get("instructions");

  const gameContainerRef = useRef(null);
  const abortControllerRef = useRef(null);
  const rootRef = useRef(null);
  const OPENAI_API_KEY = "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";
  const [activePreview, setActivePreview] = useState(activeTabValue.preview);
  const followupCount = useRef(0);
  const [followUpCountState, setFollowupCountState] = useState(0);
  const prevGeneratedCodes = useRef("");
  const [listOfgameIds, setListOfGameIds] = useState([]);
  const [isFixingCode, setIsFixingCode] = useState(false);
  const [isAddingToLibrary, setIsAddingToLibrary] = useState(false);
  const [addedLibraryUrl, setAddedLibraryUrl] = useState("");
  const [runtimeException, setRuntimeException] = useState("");
  const [themeColor, setThemeColor] = useState(
    promptEngineeringPlaygroundColorConfig[
      promptEngineeringPlaygroundClientId.general
    ]
  );

  const firstTimePrompt = useRef("");
  const handleInputChange = (e) => {
    setInputCode(e.target.value);
  };
  const [openEditPromptPopover, setOpenEditPromptPopover] = useState(false);
  const [editPromptAnchorEl, setEditPromptAnchorEl] = useState(null);
  const [systemPrompt, setSystemPrompt] = useState(
    systemPromptToGenerateInteractives
  );
  const handleGeneratedCodeChange = (code) => {
    if (isProcessing) return;
    setGeneratedJSX(code);

    if (!enableCodeValidation) {
      setIsCurrentCodePreviewed(false);
    }

    if (enableCodeValidation) {
      updatePreview(code);
    }
  };
  const [sendToParent, setSendToParent] = useState(false);
  const [themeColors, setThemeColors] = useState(
    "Primary Color: #402034, Secondary Color: #8d2d58, Accent Color: #F05A5B"
  );
  const [themeFonts, setThemeFonts] = useState("Poppins");
  const [backgroundImage, setBackgroundImage] = useState(
    DEFAULT_BACKGROUND_IMAGE_URL
  );
  const [retryCount, setRetryCount] = useState(0);
  const retryCountRef = useRef(0);
  const MAX_RETRIES = 3;
  const [disableRegenerate, setDisableRegenerate] = useState(false);
  const [disableNewSession, setDisableNewSession] = useState(false);
  const [currentGameId, setCurrentGameId] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [mode, setMode] = useState(GenerationMode.fast);
  const [currentStage, setCurrentStage] = useState(1);
  const [stageOutputs, setStageOutputs] = useState({
    learningOutcomes: null,
    reactCode: null,
    finalCode: null,
  });
  const [multiStageCodeGenPrompt, setMultiStageCodeGenPrompt] = useState(
    multiStagePrompts.codeGenPrompt
  );
  const [multiStageQaPrompt, setMultiStageQaPrompt] = useState(
    multiStagePrompts.qaPrompt
  );
  const [selectedGameType, setSelectedGameType] = useState(null);
  const selectedGameTypeRef = useRef(null);
  const [userInstructionHistory, setUserInstructionHistory] = useState([]);
  const MAX_INSTRUCTION_HISTORY = 10; // max previous instructions to keep
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [cssLoaded, setCssLoaded] = useState(false);
  const [shouldBeChargedGame, setShouldBeChargedGame] = useState(false);
  const [automaticRefine, setAutomaticRefine] = useState(false);
  const [orgId, setOrgId] = useState("");
  const [userId, setUserId] = useState("");
  const [isUsingTemplate, setIsUsingTemplate] = useState(false);
  const [enableCodeValidation, setEnableCodeValidation] = useState(true);
  const [originalGeneratedCode, setOriginalGeneratedCode] = useState("");
  const [showPreviewUpdateSuccess, setShowPreviewUpdateSuccess] =
    useState(false);
  const [isCurrentCodePreviewed, setIsCurrentCodePreviewed] = useState(true);

  useEffect(() => {
    window.parent.postMessage(
      {
        type: "isOngoingCodeGeneration",
        isProcessing: isProcessing,
      },
      "*"
    );
  }, [isProcessing]);

  useEffect(() => {
    injectExternalCss({ isLegacy: false }).then(() => {
      setCssLoaded(true);
    });
  }, []);

  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data.type === "enterEditMode") {
        const {
          id,
          code,
          followupCount: count,
          disableRegenerate,
          disableNewSession,
          gameType,
          defaultRefinePrompt,
          shouldBeCharged,
          automaticRefine,
        } = event.data.data;

        // Store the ID
        setCurrentGameId(id);
        // Set the code in the editor
        setGeneratedJSX(code);
        // Store original code for tracking edits
        setOriginalGeneratedCode(code);
        setIsCurrentCodePreviewed(true); // Code is already being previewed in edit mode
        // Update followup count
        followupCount.current = count;
        setFollowupCountState(count);
        // Disable buttons
        setDisableRegenerate(disableRegenerate);
        setDisableNewSession(disableNewSession);
        if (defaultRefinePrompt) {
          setInputCode(defaultRefinePrompt);
        }
        setIsEditMode(true); // Set edit mode to true
        if (gameType) {
          setSelectedGameType(gameType);
          selectedGameTypeRef.current = gameType;
        }
        // Show preview
        updatePreview(code);
        setShowPreview(true);
        setActivePreview(activeTabValue.preview);
        setShouldBeChargedGame(shouldBeCharged);
        setAutomaticRefine(automaticRefine);

        // Fetch interactive data from blob storage
        fetchInteractiveDataFromBlob(id)
          .then((gameData) => {
            if (!gameData) {
              throw new Error("No game data found");
            }

            // Handle question data for template-based games
            if (gameType !== GameType.SIMULATION && gameData.questionData) {
              window.currentQuestionData = gameData.questionData;
              window.currentTemplateData = {
                title: gameData.questionData.title || "Interactive Element",
                questions:
                  gameData.questionData.questions ||
                  gameData.questionData.items,
                themeColors:
                  gameData.questionData.originalThemeColors ||
                  "Primary Color: #402034, Secondary Color: #8d2d58, Accent Color: #F05A5B",
                themeFonts:
                  gameData.questionData.originalThemeFonts || "Poppins",
                backgroundImage:
                  gameData.questionData.originalBackgroundImage ||
                  DEFAULT_BACKGROUND_IMAGE_URL,
                ...gameData.questionData,
              };
            } else {
              window.currentQuestionData = null;
              window.currentTemplateData = null;
            }
          })
          .catch(() => {
            window.currentQuestionData = null;
            window.currentTemplateData = null;
          });
      }

      if (event.data.type === "AuthenticationCheck") {
        const { isAuthenticated, organizationId, presentationId, userId } =
          event.data.data;
        if (isAuthenticated) {
          setIsAuthenticated(true);
          setOrgId(organizationId);
          setUserId(userId);
        } else {
          setIsAuthenticated(false);
          setOrgId("");
          setUserId("");
        }
      }

      if (event.data.type === "CreditValidationStatus") {
        const { availableCredits } = event.data.data;
        if (availableCredits) {
          //Send a post event that the user has started generation
          window.parent.postMessage(
            {
              type: "CodeGenerationStarted",
              data: {
                isStarted: true,
              },
            },
            "*"
          );
          // single-stage logic
          streamAndRunCode();
        }
      }
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [inputCode]);

  useEffect(() => {
    window.parent.postMessage(
      {
        type: "AuthenticationCheckRequired",
      },
      "*"
    );
  }, []);

  useEffect(() => {
    if (followUpCountState > 0) {
      setEnableCodeValidation(false);
    }
  }, [followUpCountState]);

  useEffect(() => {
    window.parent.postMessage(
      {
        type: "themeDropdownState",
        data: {
          disabled: followupCount.current !== 0,
        },
      },
      "*"
    );
  }, [followupCount.current]);

  useEffect(() => {
    // Function to check if all necessary resources are loaded
    const checkIfReady = () => {
      if (
        document.readyState === "complete" && // DOM and all resources are loaded
        gameContainerRef.current && // Game container is mounted
        typeof window !== "undefined" && // Window object is available
        window.parent // Parent window is accessible
      ) {
        window.parent.postMessage({ type: "iframeReady" }, "*"); // send ready message to parent
      }
    };

    // Add event listeners for different load states
    window.addEventListener("load", checkIfReady);
    document.addEventListener("readystatechange", checkIfReady);

    // Initial check in case everything is already loaded
    checkIfReady();

    // Cleanup
    return () => {
      window.removeEventListener("load", checkIfReady);
      document.removeEventListener("readystatechange", checkIfReady);
    };
  }, []);

  useEffect(() => {
    window.parent.postMessage({ type: "iframeReady" }, "*"); // send ready message to parent
  }, []);

  // effect to listen for messages
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data.type === "themeUpdate") {
        const newColors = event.data.colors;
        const newFonts = event.data.fonts;
        const newBackgroundImage = event.data.backgroundImage;

        setThemeColors(newColors);
        setThemeFonts(newFonts);
        setBackgroundImage(newBackgroundImage || "");

        const updatedMultiStageCodeGenPrompt = replacePromptPlaceholders(
          multiStagePrompts.codeGenPrompt,
          {
            colors: newColors,
            fonts: newFonts,
          }
        );
        setMultiStageCodeGenPrompt(updatedMultiStageCodeGenPrompt);

        const updatedMultiStageQaPrompt = replacePromptPlaceholders(
          multiStagePrompts.qaPrompt,
          {
            colors: newColors,
            fonts: newFonts,
          }
        );
        setMultiStageQaPrompt(updatedMultiStageQaPrompt);
      }
    };

    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [
    mode,
    systemPromptToGenerateInteractives,
    multiStagePrompts.codeGenPrompt,
  ]);

  useEffect(() => {
    if (
      clientType === promptEngineeringPlaygroundClientId.boston &&
      promptEngineeringPlaygroundColorConfig[
        promptEngineeringPlaygroundClientId.boston
      ]
    ) {
      setThemeColor(
        promptEngineeringPlaygroundColorConfig[
          promptEngineeringPlaygroundClientId.boston
        ]
      );
    }
  }, []);

  useEffect(() => {
    window.onerror = function (message, source, lineno, colno, error) {
      console.error("Captured error:", message);
      // Handle logging or reporting the error
      return true; // Prevent the default handling of the error
    };
  }, []);

  //Generate code automatically on refine
  useEffect(() => {
    if (automaticRefine && inputCode) {
      streamAndRunCode();
    }
  }, [automaticRefine && inputCode]);

  useEffect(() => {
    if (addedLibraryUrl && addedLibraryUrl.length > 0 && sendToParent) {
      window.parent.postMessage(
        {
          type: "addedToLibrary",
          url: addedLibraryUrl,
          name: "Interactive Element",
        },
        "*"
      );
      setSendToParent(false);
    }
  }, [addedLibraryUrl]);

  useEffect(() => {
    fetch(
      "https://devprofjim.blob.core.windows.net/public-assets/interactive-gamesids.json?random=" +
        Math.floor(Math.random() * 10000000 + 1)
    )
      .then((val) => val.json())
      .then((data) => {
        if (data) {
          const allGameIds = data.map((val) => {
            return {
              name: val.name,
              id: val.id,
            };
          });
          setListOfGameIds(allGameIds);
        }
      })
      .catch((err) => {
        console.log("Error occured hwile fetchning json file", err);
      });
  }, []);

  // Add cleanup effect
  useEffect(() => {
    return () => {
      // Cleanup function to abort any ongoing requests when component unmounts
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, []);

  const uploadFiletoBlob = async (uploadContentToAzureDTO, stage) => {
    try {
      const requestURL =
        "https://dev-profjimapi-ind.azurewebsites.net/v1/UploadFileToContainer";
      const requestHeaders = new Headers();
      requestHeaders.append("Content-Type", "application/json");
      const response = await fetch(requestURL, {
        method: "POST",
        headers: requestHeaders,
        body: JSON.stringify(uploadContentToAzureDTO),
      });
      if (response.ok) {
        console.log("File Added to library");
        if (stage && stage.id) {
          const baseUrl = "https://" + window.location.host; // for local testing change it to dev-games.profjim.com with localhost it wont work on parent embed
          const url = getGameUrl(baseUrl, stage.id, selectedGameType);
          const urlObj = new URL(url);
          const searchParams = new URLSearchParams(urlObj.search);
          setAddedLibraryUrl(
            `${urlObj.origin}${urlObj.pathname}?${searchParams.toString()}`
          );
        } else {
          fetch(
            "https://devprofjim.blob.core.windows.net/public-assets/interactive-gamesids.json?random=" +
              Math.floor(Math.random() * 10000000 + 1)
          )
            .then((val) => val.json())
            .then((data) => {
              if (data) {
                const allGameIds = data.map((val) => {
                  return {
                    name: val.name,
                    id: val.id,
                  };
                });
                setListOfGameIds(allGameIds);
              }
            })
            .catch((err) => {
              console.log("Error occured hwile fetchning json file", err);
            });
        }
      }
    } catch (error) {
      console.log("error: ", error);
    } finally {
      setIsAddingToLibrary(false);
    }
  };

  const handleAddNewGameToLibrary = (askForName = true) => {
    setIsAddingToLibrary(true);
    // If in edit mode, don't ask for name and don't send to parent
    if (isEditMode) {
      const gameData = {
        id: currentGameId,
        code: automaticRefine ? prevGeneratedCodes.current : generatedJSX,
        title: "Interactive Element",
        type: selectedGameType,
      };

      // Add question data for template-based games
      if (
        selectedGameType !== GameType.SIMULATION &&
        window.currentQuestionData
      ) {
        gameData.questionData = {
          ...window.currentQuestionData,
          originalThemeColors:
            window.currentTemplateData?.themeColors || themeColors,
          originalThemeFonts:
            window.currentTemplateData?.themeFonts || themeFonts,
          originalBackgroundImage:
            window.currentTemplateData?.backgroundImage || backgroundImage,
        };
      }

      const individualJsonData = generateGamePayload(gameData);

      const finalJson = JSON.stringify(individualJsonData);
      const uploadContentToAzureDTO = {
        base64Data: window.btoa(unescape(encodeURIComponent(finalJson))),
        mimeType: "application/json",
        extension: "json",
        container: "PublicAssetsContainer",
        useRecievedFileName: true,
        fileName: currentGameId + ".json",
        shouldBeCharged: false,
      };

      uploadFiletoBlob(uploadContentToAzureDTO, individualJsonData)
        .then(() => {
          // Send message to parent window to close dialog
          window.parent.postMessage(
            {
              type: "closeDialog",
              data: {
                success: true,
                message: "Changes saved successfully",
              },
            },
            "*"
          );

          if (automaticRefine) {
            window.parent.postMessage(
              {
                type: "rebrandingCompleted",
                data: {
                  id: currentGameId,
                  status: "completed",
                  code: automaticRefine
                    ? prevGeneratedCodes.current
                    : generatedJSX,
                  title: "Interactive Element",
                  type: selectedGameType,
                },
              },
              "*"
            );
          }
        })
        .catch((error) => {
          console.error("Error saving changes:", error);
          window.parent.postMessage(
            {
              type: "closeDialog",
              data: {
                success: false,
                message: "Error saving changes",
              },
            },
            "*"
          );
          if (automaticRefine) {
            window.parent.postMessage(
              {
                type: "rebrandingCompleted",
                data: {
                  id: currentGameId,
                  status: "failed",
                  code: automaticRefine
                    ? prevGeneratedCodes.current
                    : generatedJSX,
                  title: "Interactive Element",
                  type: selectedGameType,
                },
              },
              "*"
            );
          }
        })
        .finally(() => {
          setIsAddingToLibrary(false);
        });
      return;
    }

    const copiedListOfGames = JSON.parse(JSON.stringify(listOfgameIds));
    let componentName = "";
    if (askForName) {
      componentName = window.prompt(
        "Enter the name of this interactive element: "
      );
      if (!componentName) {
        componentName = "Interactive Element";
      }
    } else {
      componentName = inputCode;
      setSendToParent(true);
    }

    const newFileDto = {
      id: currentGameId || uuidv4(),
      name: componentName,
    };

    copiedListOfGames.push(newFileDto);

    const gameData = {
      id: newFileDto.id,
      code: generatedJSX,
      title: componentName,
      type: selectedGameType,
    };

    // Add question data for template-based games
    if (
      selectedGameType !== GameType.SIMULATION &&
      window.currentQuestionData
    ) {
      gameData.questionData = {
        ...window.currentQuestionData,
        originalThemeColors:
          window.currentTemplateData?.themeColors || themeColors,
        originalThemeFonts:
          window.currentTemplateData?.themeFonts || themeFonts,
        originalBackgroundImage:
          window.currentTemplateData?.backgroundImage || backgroundImage,
      };
    }

    const individualJsonData = generateGamePayload(gameData);

    //For individual game
    const finalJson = JSON.stringify(individualJsonData);
    const uploadContentToAzureDTO = {
      base64Data: window.btoa(unescape(encodeURIComponent(finalJson))),
      mimeType: "application/json",
      extension: "json",
      container: "PublicAssetsContainer",
      useRecievedFileName: true,
      fileName: newFileDto.id + ".json" || "random.json",
    };

    uploadFiletoBlob(uploadContentToAzureDTO, individualJsonData);
  };

  const fixPastedOrIncorrectCodeUsingGPT = async (jsxCode) => {
    setIsFixingCode(true);

    let modelToUse = "claude-3-5-sonnet-20241022"; // fallback
    if (orgId && userId) {
      try {
        const promptService = new PromptService(orgId, userId, "", "", "");
        await promptService.getSystemPrompt();
        modelToUse = await promptService.getModel();
      } catch (error) {
        console.warn("Failed to get model, using fallback");
      }
    }

    const aiModelRequest = {
      prompt: `
      #########################
      React code - 

      ${jsxCode}`,
      GPTResponseType: 1,
      systemPrompt: `Combine the given React code in a single component named 'InteractiveElement'. The 'InteractiveElement' component should act as the entry point for the application. Do not alter any other logic in the existing user provided code. The InteractiveElement component should execute all other components in the order in which they are present in the original user provided React code. "InteractiveElement" should be a functional component. Remove import and export statements if present. Return only JSX code without any commentry.`,
      requestType: 2,
      model: modelToUse,
      includeModerations: true,
    };

    try {
      const response = await axios.post(
        "https://testing-profjimapi.azurewebsites.net/v1/OpenAI/ChatGPTRequest",
        aiModelRequest,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      const stringifiedResponse = response.data.data.result;
      if (stringifiedResponse) {
        const parsedResponse = JSON.parse(stringifiedResponse);
        if (
          parsedResponse &&
          parsedResponse.content &&
          parsedResponse.content[0]
        ) {
          const generatedCode = parsedResponse.content[0].text;
          followupCount.current = followupCount.current + 1;
          setFollowupCountState(followupCount.current);
          setGeneratedJSX(generatedCode);
          try {
            updatePreview(generatedCode);
            setShowPreview(true);
          } catch (evalError) {
            console.error("Error evaluating generated code:", evalError);
            setError(`Error in generated code: ${evalError.message}`);
          }
        } else {
          throw new Error("Unexpected response structure from API");
        }
      } else {
        throw new Error("Empty response from API");
      }
    } catch (err) {
      console.error("Error generating or running code:", err);
      if (axios.isAxiosError(err)) {
        if (err.response) {
          setError(
            `API Error: ${err.response.status} - ${
              err.response.data.error?.message || "Unknown error"
            }`
          );
        } else if (err.request) {
          setError(
            "No response received from the API. Please check your internet connection."
          );
        } else {
          setError(`Error setting up the request: ${err.message}`);
        }
      } else {
        setError(err.toString());
      }
    } finally {
      setIsFixingCode(false);
    }
  };

  function cleanGeneratedCode(code) {
    // Remove any leading or trailing backticks and 'jsx' tag
    code = code.replace(/^```jsx?|```$/g, "").trim();

    const firstIndexOfInteractiveComponent = code.indexOf(
      "const InteractiveElement = () =>"
    );

    if (firstIndexOfInteractiveComponent > -1) {
      code = code.slice(firstIndexOfInteractiveComponent);
    }

    // Count opening and closing braces to ensure balance
    let braceCount = 0;
    let cleanedCode = "";
    for (let char of code) {
      if (char === "{") braceCount++;
      if (char === "}") braceCount--;
      cleanedCode += char;

      // Stop if we've reached a balanced state and encountered the last closing brace
      if (braceCount === 0 && char === "}") break;
    }

    // Ensure the code ends with a single closing brace
    cleanedCode = cleanedCode.replace(/}+$/, "}");

    prevGeneratedCodes.current = cleanedCode;
    setGeneratedJSX(cleanedCode);
    return cleanedCode;
  }

  function executeTranspiledCode(code) {
    const cleanedCode = cleanGeneratedCode(code);
    try {
      const transformedCode = Babel.transform(cleanedCode, {
        presets: ["react"],
      }).code;

      const execFunction = new Function(
        "React",
        "ReactDOM",
        "styled",
        "animated",
        "useSpring",
        "useState",
        "useEffect",
        "useCallback",
        "useMemo",
        "ComposableMap",
        "Geographies",
        "Geography",
        "Marker",
        "scaleQuantile",
        "Tooltip",
        "Button",
        "Line",
        "motion",
        "AnimatePresence",
        "useRef",
        "p5",
        "LucideIcons",
        "Recharts",
        "Card",
        "CardContent",
        "CardHeader",
        "CardTitle",
        "ShadButton",
        "Slider",
        "Input",
        "Label",
        "Badge",
        "Accordion",
        "AccordionContent",
        "AccordionItem",
        "AccordionTrigger",
        "Alert",
        "AlertDescription",
        "AlertTitle",
        "AlertDialog",
        "AlertDialogAction",
        "AlertDialogCancel",
        "AlertDialogContent",
        "AlertDialogDescription",
        "AlertDialogFooter",
        "AlertDialogHeader",
        "AlertDialogOverlay",
        "AlertDialogPortal",
        "AlertDialogTitle",
        "AlertDialogTrigger",
        "AspectRatio",
        "Avatar",
        "AvatarFallback",
        "AvatarImage",
        "Breadcrumb",
        "BreadcrumbEllipsis",
        "BreadcrumbItem",
        "BreadcrumbLink",
        "BreadcrumbList",
        "BreadcrumbPage",
        "BreadcrumbSeparator",
        "Calendar",
        "Carousel",
        "CarouselContent",
        "CarouselItem",
        "CarouselNext",
        "CarouselPrevious",
        "Checkbox",
        "Collapsible",
        "CollapsibleContent",
        "CollapsibleTrigger",
        "ComboboxDemo",
        "Command",
        "CommandDialog",
        "CommandEmpty",
        "CommandGroup",
        "CommandInput",
        "CommandItem",
        "CommandList",
        "CommandSeparator",
        "CommandShortcut",
        "ContextMenu",
        "ContextMenuCheckboxItem",
        "ContextMenuContent",
        "ContextMenuGroup",
        "ContextMenuItem",
        "ContextMenuLabel",
        "ContextMenuPortal",
        "ContextMenuRadioGroup",
        "ContextMenuRadioItem",
        "ContextMenuSeparator",
        "ContextMenuShortcut",
        "ContextMenuSub",
        "ContextMenuSubContent",
        "ContextMenuSubTrigger",
        "ContextMenuTrigger",
        "Dialog",
        "DialogClose",
        "DialogContent",
        "DialogDescription",
        "DialogFooter",
        "DialogHeader",
        "DialogOverlay",
        "DialogPortal",
        "DialogTitle",
        "DialogTrigger",
        "Drawer",
        "DrawerClose",
        "DrawerContent",
        "DrawerDescription",
        "DrawerFooter",
        "DrawerHeader",
        "DrawerOverlay",
        "DrawerPortal",
        "DrawerTitle",
        "DrawerTrigger",
        "DropdownMenu",
        "DropdownMenuCheckboxItem",
        "DropdownMenuContent",
        "DropdownMenuGroup",
        "DropdownMenuItem",
        "DropdownMenuLabel",
        "DropdownMenuPortal",
        "DropdownMenuRadioGroup",
        "DropdownMenuRadioItem",
        "DropdownMenuSeparator",
        "DropdownMenuShortcut",
        "DropdownMenuSub",
        "DropdownMenuSubContent",
        "DropdownMenuSubTrigger",
        "DropdownMenuTrigger",
        "Form",
        "FormControl",
        "FormDescription",
        "FormField",
        "FormItem",
        "FormLabel",
        "FormMessage",
        "useFormField",
        "HoverCard",
        "HoverCardContent",
        "HoverCardTrigger",
        "InputOTP",
        "InputOTPGroup",
        "InputOTPSeparator",
        "InputOTPSlot",
        "Menubar",
        "MenubarCheckboxItem",
        "MenubarContent",
        "MenubarGroup",
        "MenubarItem",
        "MenubarLabel",
        "MenubarMenu",
        "MenubarPortal",
        "MenubarRadioGroup",
        "MenubarRadioItem",
        "MenubarSeparator",
        "MenubarShortcut",
        "MenubarSub",
        "MenubarSubContent",
        "MenubarSubTrigger",
        "MenubarTrigger",
        "Pagination",
        "PaginationContent",
        "PaginationEllipsis",
        "PaginationItem",
        "PaginationLink",
        "PaginationNext",
        "PaginationPrevious",
        "Popover",
        "PopoverContent",
        "PopoverTrigger",
        "Progress",
        "RadioGroup",
        "RadioGroupItem",
        "ResizableHandle",
        "ResizablePanel",
        "ResizablePanelGroup",
        "Select",
        "SelectContent",
        "SelectGroup",
        "SelectItem",
        "SelectLabel",
        "SelectScrollDownButton",
        "SelectScrollUpButton",
        "SelectSeparator",
        "SelectTrigger",
        "SelectValue",
        "Separator",
        "Sheet",
        "SheetClose",
        "SheetContent",
        "SheetDescription",
        "SheetFooter",
        "SheetHeader",
        "SheetOverlay",
        "SheetPortal",
        "SheetTitle",
        "SheetTrigger",
        "Skeleton",
        "Toaster",
        "Switch",
        "Table",
        "TableBody",
        "TableCaption",
        "TableCell",
        "TableFooter",
        "TableHead",
        "TableHeader",
        "TableRow",
        "Tabs",
        "TabsContent",
        "TabsList",
        "TabsTrigger",
        "Textarea",
        "Toast",
        "ToastAction",
        "ToastClose",
        "ToastDescription",
        "ToastProvider",
        "ToastTitle",
        "ToastViewport",
        "Toggle",
        "toggleVariants",
        "ToggleGroup",
        "ToggleGroupItem",
        "TooltipContent",
        "TooltipProvider",
        "TooltipTrigger",
        "html2Canvas",
        "Moveable",
        "MathJax",
        "MathJaxContext",
        "mathJaxConfig",
        "trackEvent",
        "THREE",
        "confetti",
        "DOMPurify",
        "MathText",
        `${transformedCode}
        return InteractiveElement;`
      );

      setError(null);
      return execFunction(
        React,
        ReactDOM,
        styled,
        animated,
        useSpring,
        useState,
        useEffect,
        useCallback,
        useMemo,
        ComposableMap,
        Geographies,
        Geography,
        Marker,
        scaleQuantile,
        Tooltip,
        ShadButton,
        Line,
        motion,
        AnimatePresence,
        useRef,
        p5,
        LucideIcons,
        Recharts,
        Card,
        CardContent,
        CardHeader,
        CardTitle,
        ShadButton,
        Slider,
        Input,
        Label,
        Badge,
        Accordion,
        AccordionContent,
        AccordionItem,
        AccordionTrigger,
        Alert,
        AlertDescription,
        AlertTitle,
        AlertDialog,
        AlertDialogAction,
        AlertDialogCancel,
        AlertDialogContent,
        AlertDialogDescription,
        AlertDialogFooter,
        AlertDialogHeader,
        AlertDialogOverlay,
        AlertDialogPortal,
        AlertDialogTitle,
        AlertDialogTrigger,
        AspectRatio,
        Avatar,
        AvatarFallback,
        AvatarImage,
        Breadcrumb,
        BreadcrumbEllipsis,
        BreadcrumbItem,
        BreadcrumbLink,
        BreadcrumbList,
        BreadcrumbPage,
        BreadcrumbSeparator,
        Calendar,
        Carousel,
        CarouselContent,
        CarouselItem,
        CarouselNext,
        CarouselPrevious,
        Checkbox,
        Collapsible,
        CollapsibleContent,
        CollapsibleTrigger,
        ComboboxDemo,
        Command,
        CommandDialog,
        CommandEmpty,
        CommandGroup,
        CommandInput,
        CommandItem,
        CommandList,
        CommandSeparator,
        CommandShortcut,
        ContextMenu,
        ContextMenuCheckboxItem,
        ContextMenuContent,
        ContextMenuGroup,
        ContextMenuItem,
        ContextMenuLabel,
        ContextMenuPortal,
        ContextMenuRadioGroup,
        ContextMenuRadioItem,
        ContextMenuSeparator,
        ContextMenuShortcut,
        ContextMenuSub,
        ContextMenuSubContent,
        ContextMenuSubTrigger,
        ContextMenuTrigger,
        Dialog,
        DialogClose,
        DialogContent,
        DialogDescription,
        DialogFooter,
        DialogHeader,
        DialogOverlay,
        DialogPortal,
        DialogTitle,
        DialogTrigger,
        Drawer,
        DrawerClose,
        DrawerContent,
        DrawerDescription,
        DrawerFooter,
        DrawerHeader,
        DrawerOverlay,
        DrawerPortal,
        DrawerTitle,
        DrawerTrigger,
        DropdownMenu,
        DropdownMenuCheckboxItem,
        DropdownMenuContent,
        DropdownMenuGroup,
        DropdownMenuItem,
        DropdownMenuLabel,
        DropdownMenuPortal,
        DropdownMenuRadioGroup,
        DropdownMenuRadioItem,
        DropdownMenuSeparator,
        DropdownMenuShortcut,
        DropdownMenuSub,
        DropdownMenuSubContent,
        DropdownMenuSubTrigger,
        DropdownMenuTrigger,
        Form,
        FormControl,
        FormDescription,
        FormField,
        FormItem,
        FormLabel,
        FormMessage,
        useFormField,
        HoverCard,
        HoverCardContent,
        HoverCardTrigger,
        InputOTP,
        InputOTPGroup,
        InputOTPSeparator,
        InputOTPSlot,
        Menubar,
        MenubarCheckboxItem,
        MenubarContent,
        MenubarGroup,
        MenubarItem,
        MenubarLabel,
        MenubarMenu,
        MenubarPortal,
        MenubarRadioGroup,
        MenubarRadioItem,
        MenubarSeparator,
        MenubarShortcut,
        MenubarSub,
        MenubarSubContent,
        MenubarSubTrigger,
        MenubarTrigger,
        Pagination,
        PaginationContent,
        PaginationEllipsis,
        PaginationItem,
        PaginationLink,
        PaginationNext,
        PaginationPrevious,
        Popover,
        PopoverContent,
        PopoverTrigger,
        Progress,
        RadioGroup,
        RadioGroupItem,
        ResizableHandle,
        ResizablePanel,
        ResizablePanelGroup,
        Select,
        SelectContent,
        SelectGroup,
        SelectItem,
        SelectLabel,
        SelectScrollDownButton,
        SelectScrollUpButton,
        SelectSeparator,
        SelectTrigger,
        SelectValue,
        Separator,
        Sheet,
        SheetClose,
        SheetContent,
        SheetDescription,
        SheetFooter,
        SheetHeader,
        SheetOverlay,
        SheetPortal,
        SheetTitle,
        SheetTrigger,
        Skeleton,
        Toaster,
        ShadSwitch,
        Table,
        TableBody,
        TableCaption,
        TableCell,
        TableFooter,
        TableHead,
        TableHeader,
        TableRow,
        Tabs,
        TabsContent,
        TabsList,
        TabsTrigger,
        Textarea,
        Toast,
        ToastAction,
        ToastClose,
        ToastDescription,
        ToastProvider,
        ToastTitle,
        ToastViewport,
        Toggle,
        toggleVariants,
        ToggleGroup,
        ToggleGroupItem,
        TooltipContent,
        TooltipProvider,
        TooltipTrigger,
        html2Canvas,
        Moveable,
        MathJax,
        MathJaxContext,
        mathJaxConfig,
        trackEvent,
        THREE,
        confetti,
        DOMPurify,
        MathText
      );
    } catch (error) {
      console.error("Error in code transformation or execution:", error);
      throw new Error(`Failed to execute code: ${error.message}`);
    }
  }

  const updatePreview = (code, isManualUpdate = false) => {
    try {
      const InteractiveElement = executeTranspiledCode(code);
      if (typeof InteractiveElement !== "function") {
        throw new Error(
          "The generated code did not return a valid React component"
        );
      }
      setOutput(true);
      setGeneratedComponent(() => InteractiveElement);
      setShowPreview(true);
      setError(null);

      // Mark current code as previewed
      setIsCurrentCodePreviewed(true);

      // Show success message for manual updates
      if (isManualUpdate) {
        setShowPreviewUpdateSuccess(true);
        setTimeout(() => {
          setShowPreviewUpdateSuccess(false);
        }, 3000); // Hide message after 3 seconds
      }
    } catch (evalError) {
      console.error("Error evaluating generated code:", evalError);
      if (!enableCodeValidation) {
        setError(
          `Code error: ${evalError.message}. Please check your code and toggle to preview to see the result.`
        );
        setOutput(false);
        setGeneratedComponent(null);
      } else {
        handleError(evalError);
      }
    }
  };

  useEffect(() => {
    if (showPreview && generatedComponent && gameContainerRef.current) {
      try {
        if (!rootRef.current) {
          rootRef.current = createRoot(gameContainerRef.current);
        }
        rootRef.current.render(React.createElement(generatedComponent));
      } catch (err) {
        console.log("Error occured: ", err);
      }
    }
    return () => {
      if (rootRef.current) {
        rootRef.current.unmount();
        rootRef.current = null;
      }
    };
  }, [showPreview, generatedComponent]);

  const GameTypeSelector = () => {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const handleClick = (event) => {
      if (followupCount.current > 0) return;
      setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
      setAnchorEl(null);
    };

    const handleSelect = async (value) => {
      setSelectedGameType(value);
      selectedGameTypeRef.current = value;
      handleClose();
    };

    const getDisplayText = () => {
      if (!selectedGameType) return "Select type";
      if (selectedGameType === "none" && isEditMode) return "Any";
      return gameTypeLabels[selectedGameType] || "Select type";
    };

    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginTop: "12px",
          marginBottom: "12px",
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontSize: "14px",
            fontFamily: themeColor.primaryFont,
            color: themeColor.headerColor,
          }}
        >
          Exercise type
        </Typography>
        <div style={{ width: "260px" }}>
          <Button
            onClick={handleClick}
            disabled={followupCount.current > 0}
            sx={{
              width: "100%",
              height: "34px",
              backgroundColor: profJimBrandColors.white,
              border: `1px solid ${profJimBrandColors.purple}`,
              color: profJimBrandColors.grey,
              textTransform: "none",
              justifyContent: "space-between",
              padding: "4px 14px",
              "&:hover": {
                backgroundColor: profJimBrandColors.white,
                border: `1px solid ${profJimBrandColors.purple}`,
              },
              "&.Mui-disabled": {
                backgroundColor: "rgba(255, 255, 255, 0.3)",
                color: `${profJimBrandColors.grey}80`,
                border: `1px solid ${profJimBrandColors.purple}40`,
                cursor: "not-allowed",
              },
            }}
            endIcon={<ArrowDropDown />}
          >
            <span
              style={{
                fontSize: "14px",
                fontFamily: themeColor.primaryFont,
                color: "inherit",
              }}
            >
              {getDisplayText()}
            </span>
          </Button>
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            PaperProps={{
              elevation: 0,
              sx: {
                width: "260px",
                maxHeight: "350px",
                overflow: "auto",
                filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
                mt: 1.5,
                backgroundColor: profJimBrandColors.white,
                "&:before": {
                  content: '""',
                  display: "block",
                  position: "absolute",
                  top: 0,
                  left: 14,
                  width: 10,
                  height: 10,
                  bgcolor: "background.paper",
                  transform: "translateY(-50%) rotate(45deg)",
                  zIndex: 0,
                },
                "&::-webkit-scrollbar": {
                  width: "8px",
                },
                "&::-webkit-scrollbar-track": {
                  background: `${profJimBrandColors.purple}10`,
                  borderRadius: "4px",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: `${profJimBrandColors.purple}40`,
                  borderRadius: "4px",
                  "&:hover": {
                    background: `${profJimBrandColors.purple}60`,
                  },
                },
              },
            }}
            transformOrigin={{ horizontal: "left", vertical: "top" }}
            anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
          >
            {Object.entries(gameTypeLabels).map(([value, label]) => (
              <MenuItem
                key={value}
                onClick={() => handleSelect(value)}
                selected={value === selectedGameType}
                disabled={followupCount.current > 0}
                sx={{
                  fontSize: "14px",
                  fontFamily: themeColor.primaryFont,
                  padding: "8px 16px",
                  "&:hover": {
                    backgroundColor: `${profJimBrandColors.purple}20`,
                  },
                  "&.Mui-selected": {
                    backgroundColor: `${profJimBrandColors.purple}40`,
                    "&:hover": {
                      backgroundColor: `${profJimBrandColors.purple}40`,
                    },
                  },
                  "&.Mui-disabled": {
                    opacity: 0.5,
                    backgroundColor: "transparent",
                  },
                }}
              >
                {label}
              </MenuItem>
            ))}
          </Menu>
        </div>
      </div>
    );
  };

  // force fast mode if multi-stage is not enabled
  useEffect(() => {
    if (!isMultiStage) {
      setMode(GenerationMode.fast);
    }
  }, []);

  const ModeSelector = () => {
    // if not multi-stage, don't show the mode selector
    if (!isMultiStage) return null;
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginTop: "12px",
          marginBottom: "12px",
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: "bold",
            fontSize: "14px",
            fontFamily: themeColor.primaryFont,
            color: themeColor.headerColor,
          }}
        >
          Generation mode:
        </Typography>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "5px",
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontSize: "14px",
              fontFamily: themeColor.primaryFont,
              color: themeColor.headerColor,
            }}
          >
            Fast
          </Typography>
          <Switch
            checked={mode === GenerationMode.quality}
            onChange={(e) =>
              setMode(
                e.target.checked ? GenerationMode.quality : GenerationMode.fast
              )
            }
            disabled={followupCount.current > 0}
            sx={{
              "& .Mui-checked+.MuiSwitch-track": {
                backgroundColor: profJimBrandColors.purple + "!important",
              },
              "& .MuiSwitch-track": {
                backgroundColor:
                  followupCount.current > 0
                    ? `${profJimBrandColors.purple}40!important`
                    : profJimBrandColors.purple + "!important",
              },
              "& .MuiSwitch-thumb": {
                backgroundColor:
                  followupCount.current > 0
                    ? `${profJimBrandColors.purple}40`
                    : profJimBrandColors.purple,
                transition: "transform 150ms cubic-bezier(0.4, 0, 0.2, 1)",
              },
              "& .MuiSwitch-switchBase": {
                transition:
                  "left 150ms cubic-bezier(0.4, 0, 0.2, 1), transform 150ms cubic-bezier(0.4, 0, 0.2, 1)",
                "&.Mui-disabled": {
                  "& + .MuiSwitch-track": {
                    opacity: 0.3,
                  },
                  "& .MuiSwitch-thumb": {
                    opacity: 0.3,
                  },
                },
              },
            }}
          />
          <Typography
            variant="h5"
            sx={{
              fontSize: "14px",
              fontFamily: themeColor.primaryFont,
              color: themeColor.headerColor,
            }}
          >
            Quality
          </Typography>
        </div>
      </div>
    );
  };

  const userInstructionHistoryTracker =
    userInstructionHistory.length === 0 ? null : (
      <Box
        sx={{
          marginTop: "20px",
          backgroundColor: `${profJimBrandColors.purple}10`,
          borderRadius: "8px",
          padding: "12px",
        }}
      >
        <Typography
          variant="h6"
          sx={{
            color: themeColor.headerColor,
            fontSize: "14px",
            fontFamily: themeColor.primaryFont,
            marginBottom: "8px",
          }}
        >
          Previous instructions
        </Typography>
        {userInstructionHistory.map((item) => (
          <Box
            key={item.id}
            sx={{
              padding: "8px",
              marginBottom: "8px",
              backgroundColor: profJimBrandColors.white,
              borderRadius: "4px",
              cursor: "pointer",
              border: `1px solid ${profJimBrandColors.purple}20`,
              "&:hover": {
                backgroundColor: `${profJimBrandColors.purple}10`,
              },
            }}
            onClick={() => {
              setInputCode(item.instruction);
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: themeColor.instructionColor,
                fontSize: "14px",
                fontFamily: themeColor.primaryFont,
              }}
            >
              {item.instruction}
            </Typography>
          </Box>
        ))}
      </Box>
    );

  const getPlaceholderText = () => {
    if (followUpCountState > 0) {
      const currentSelections = window.currentGenerationParams;
      let effectiveGameType =
        currentSelections?.gameType || selectedGameTypeRef.current;

      // Handle edit mode when gameType is 'none' or undefined - default to simulation
      if (isEditMode && (!effectiveGameType || effectiveGameType === "none")) {
        effectiveGameType = GameType.SIMULATION;
      }

      if (effectiveGameType === GameType.SIMULATION) {
        return "Enter instructions to refine your interactive (e.g., add more questions, change the colors).";
      } else {
        return "Enter instructions to refine the content (e.g., add more questions, change difficulty).";
      }
    } else if (selectedGameType === GameType.SIMULATION) {
      return "Enter a topic or idea for an interactive learning experience. Note that simulations work best for STEM topics.";
    } else {
      return "Enter a topic or idea for an interactive learning experience.";
    }
  };

  const handleGenerateCode = async () => {
    // Cancel any existing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    // Create new abort controller for this request
    abortControllerRef.current = new AbortController();

    if (mode === GenerationMode.fast) {
      const currentSelections = {
        gameType: selectedGameType,
        themeColors,
        themeFonts,
        backgroundImage,
      };
      window.currentGenerationParams = currentSelections;

      //Send message to validate credits
      window.parent.postMessage(
        {
          type: "CheckForCredits",
        },
        "*"
      );
    } else {
      await handleQualityModeGeneration();
    }
  };

  const makeAIRequest = async ({ prompt, systemPrompt, model, signal }) => {
    if (
      model === AI_MODELS.sonnet ||
      model === AI_MODELS.sonnet37 ||
      model === AI_MODELS.sonnet4
    ) {
      const messages = [
        { role: "assistant", content: systemPrompt },
        { role: "user", content: prompt },
      ];

      try {
        const response = await anthropic.messages.create({
          messages: messages,
          model: model,
          max_tokens: 8192,
        });

        return response.content[0].text;
      } catch (error) {
        throw new Error(`Claude API Error: ${error.message}`);
      }
    }
    // For API-based requests (GPT-4o)
    else {
      const aiModelRequest = {
        prompt: prompt,
        GPTResponseType: 1,
        systemPrompt: systemPrompt,
        requestType: 1,
        model: "gpt-4o",
        includeModerations: true,
      };

      try {
        const response = await axios.post(
          "https://testing1-profjimapi.azurewebsites.net/v1/OpenAI/ChatGPTRequest",
          aiModelRequest,
          {
            headers: {
              "Content-Type": "application/json",
            },
            signal: signal,
          }
        );

        const stringifiedResponse = response.data.data.result;
        if (!stringifiedResponse) {
          throw new Error("Empty response from API");
        }

        return stringifiedResponse;
      } catch (err) {
        if (axios.isAxiosError(err)) {
          if (err.response) {
            throw new Error(
              `API Error: ${err.response.status} - ${
                err.response.data.error?.message || "Unknown error"
              }`
            );
          } else if (err.request) {
            throw new Error(
              "No response received from the API. Please check your internet connection."
            );
          } else {
            throw new Error(`Error setting up the request: ${err.message}`);
          }
        }
        throw err;
      }
    }
  };

  const getModifiedPrompt = (
    originalPrompt,
    gameType,
    effectiveThemeColors,
    effectiveThemeFonts
  ) => {
    if (!gameType) return originalPrompt;

    let modifiedSystemPrompt = originalPrompt;

    if (gameType === GameType.SIMULATION && followupCount.current === 0) {
      const modifiedSimulationSystemPrompt = replacePromptPlaceholders(
        modifiedSystemPrompt,
        {
          colors: effectiveThemeColors,
          fonts: effectiveThemeFonts,
        }
      );
      return modifiedSimulationSystemPrompt;
    } else if (gameType === GameType.SIMULATION && followupCount.current > 0) {
      const modifiedSimulationSystemPrompt = replacePromptPlaceholders(
        modifiedSystemPrompt,
        {
          colors: "",
          fonts: "",
        }
      );
      return modifiedSimulationSystemPrompt;
    } else if (
      gameType !== GameType.SIMULATION &&
      followupCount.current === 0
    ) {
      modifiedSystemPrompt = replacePromptPlaceholders(modifiedSystemPrompt, {
        colors: effectiveThemeColors,
        fonts: effectiveThemeFonts,
      });
    } else if (gameType !== GameType.SIMULATION && followupCount.current > 0) {
      modifiedSystemPrompt = replacePromptPlaceholders(modifiedSystemPrompt, {
        colors: "",
        fonts: "",
      });
    }

    const gameTypeInstruction = `Create a "${gameTypeLabels[gameType]}" type interactive game on the given topic/learning outcome.`;

    // const newPrompt = modifiedSystemPrompt.replace(
    //   `${gameTypeRequirements[GameType.TRUE_FALSE]}`,
    //   `${gameTypeRequirements[gameType]}`
    // );

    return `${gameTypeInstruction}\n\n${modifiedSystemPrompt}`;
  };

  const handleQualityModeGeneration = async () => {
    try {
      setIsProcessing(true);
      setError(null);
      setOutput(null);
      setGeneratedJSX("");
      setActivePreview(activeTabValue.code);
      setShowPreview(false);

      if (followupCount.current === 0) {
        firstTimePrompt.current += inputCode;
      }
      followupCount.current += 1;

      const modifiedLOPrompt = getModifiedPrompt(
        multiStagePrompts.learningOutcomesPrompt,
        selectedGameType
      );

      const promptService = new PromptService(orgId, userId, "", "", "");
      await promptService.getSystemPrompt();
      const adminModel = await promptService.getModel();

      // Stage 1: Learning Outcomes
      setCurrentStage(1);
      const learningOutcomes = await makeAIRequest({
        prompt: inputCode ? inputCode : firstTimePrompt.current,
        systemPrompt: modifiedLOPrompt,
        model: adminModel,
        signal: abortControllerRef.current?.signal,
      });
      setStageOutputs((prev) => ({ ...prev, learningOutcomes }));

      // Stage 2: React Implementation
      setCurrentStage(2);
      const reactCode = await makeAIRequest({
        prompt: learningOutcomes,
        systemPrompt: multiStageCodeGenPrompt,
        model: adminModel,
        signal: abortControllerRef.current?.signal,
      });
      setStageOutputs((prev) => ({ ...prev, reactCode }));

      // Stage 3: QA
      setCurrentStage(3);

      let finalCode = "";
      const messages = [
        { role: "assistant", content: multiStageQaPrompt },
        { role: "user", content: reactCode },
      ];

      await streamAnthropicMessages({
        messages,
        onText: (text) => {
          finalCode += text;
          setGeneratedJSX((prevCode) => prevCode + text);
        },
        onEnd: async () => {
          try {
            setFollowupCountState(followupCount.current);

            if (!finalCode.endsWith("}") || !finalCode.endsWith("};")) {
              finalCode += "}";
            }
            prevGeneratedCodes.current = finalCode;

            setTimeout(async () => {
              await updatePreview(finalCode);
              setShowPreview(true);
              setActivePreview(activeTabValue.preview);
              setInputCode("");
              setIsProcessing(false);
              setCurrentStage(1);
              setOriginalGeneratedCode(finalCode);
            }, 500);
          } catch (previewError) {
            console.error("Preview error:", previewError);
            handleError(previewError);
          }
        },
        onError: (error) => {
          console.error("Stream error:", error);
          handleError(error);
          setIsProcessing(false);
          setCurrentStage(1);
        },
      });
    } catch (error) {
      console.error("Error in quality mode generation:", error);
      if (error.name === "AbortError") {
        setError("Generation cancelled");
      } else {
        handleError(error);
      }
      setIsProcessing(false);
      setCurrentStage(1);
    }
  };

  // stage descriptions
  const getStageDescription = (stage) => {
    switch (stage) {
      case 1:
        return "Planning...";
      case 2:
        return "Coding...";
      case 3:
        return "Polishing...";
      default:
        return "Processing...";
    }
  };

  const generateTemplateBasedInteractive = async (
    isRetry = false,
    errorMessage = null
  ) => {
    try {
      setIsProcessing(true);
      setError(null);
      setOutput(null);
      setGeneratedJSX("");
      setActivePreview(activeTabValue.code);
      setShowPreview(false);

      if (!isRetry) {
        retryCountRef.current = 0;
        setRetryCount(0);
      }

      let generatedCode = "";
      const currentSelections = window.currentGenerationParams;
      let effectiveGameType =
        currentSelections?.gameType || selectedGameTypeRef.current;

      if (isEditMode && (!effectiveGameType || effectiveGameType === "none")) {
        effectiveGameType = GameType.SIMULATION;
      }

      const effectiveThemeColors =
        isEditMode && window.currentTemplateData?.themeColors
          ? window.currentTemplateData.themeColors
          : currentSelections?.themeColors || themeColors;
      const effectiveThemeFonts =
        isEditMode && window.currentTemplateData?.themeFonts
          ? window.currentTemplateData.themeFonts
          : currentSelections?.themeFonts || themeFonts;
      const effectiveBackgroundImage =
        isEditMode && window.currentTemplateData?.backgroundImage
          ? window.currentTemplateData.backgroundImage
          : currentSelections?.backgroundImage || backgroundImage;

      // Handle first-time vs refinement/edit mode
      if (followupCount.current === 0 && !isEditMode) {
        firstTimePrompt.current += inputCode;
        setIsUsingTemplate(true);

        try {
          const templateResult =
            await templateManager.generateTemplateBasedCode(
              effectiveGameType,
              inputCode ? inputCode : firstTimePrompt.current,
              effectiveThemeColors,
              effectiveThemeFonts,
              effectiveBackgroundImage,
              orgId,
              userId
            );

          generatedCode = templateResult.code;

          window.currentTemplateData = templateResult.templateData;
          window.currentQuestionData = templateResult.questionData;
        } catch (templateError) {
          setIsUsingTemplate(false);
          await streamCode();
          return;
        }
      } else {
        // Refinement or Edit Mode
        setIsUsingTemplate(true);

        if (!window.currentTemplateData || !window.currentQuestionData) {
          setIsUsingTemplate(false);
          await streamCode();
          return;
        }

        const refinementInstruction =
          inputCode.trim() ||
          "Improve the questions by making them more engaging, clearer, and educationally effective while keeping the same topic and difficulty level. Ensure questions are well-structured and explanations are comprehensive.";

        try {
          const refinedQuestionData = await templateManager.refineQuestionData(
            effectiveGameType,
            window.currentQuestionData,
            refinementInstruction,
            orgId,
            userId
          );

          const updatedTemplateData = {
            ...window.currentTemplateData,
            title:
              refinedQuestionData.title || window.currentTemplateData.title,
            questions:
              refinedQuestionData.questions || refinedQuestionData.items,
            themeColors: window.currentTemplateData.themeColors,
            themeFonts: window.currentTemplateData.themeFonts,
            backgroundImage: window.currentTemplateData.backgroundImage,
            ...refinedQuestionData,
          };

          generatedCode = await templateManager.getTemplateCodeFromAdmin(
            effectiveGameType,
            updatedTemplateData,
            orgId,
            userId
          );

          if (!generatedCode) {
            setIsUsingTemplate(false);
            await streamCode();
            return;
          }

          window.currentTemplateData = updatedTemplateData;
          window.currentQuestionData = refinedQuestionData;
        } catch (refinementError) {
          setIsUsingTemplate(false);
          await streamCode();
          return;
        }
      }

      setGeneratedJSX(generatedCode);
      followupCount.current += 1;
      setFollowupCountState(followupCount.current);
      prevGeneratedCodes.current = generatedCode;

      await updatePreview(generatedCode);
      setShowPreview(true);
      setActivePreview(activeTabValue.preview);
      setInputCode("");
      setIsProcessing(false);
      setIsUsingTemplate(false);
      window.currentGenerationParams = null;
      setOriginalGeneratedCode(generatedCode);

      // Handle automatic refinement in edit mode
      if (automaticRefine && isEditMode) {
        handleAddNewGameToLibrary(false);
      }
    } catch (templateError) {
      setIsUsingTemplate(false);
      await streamCode();
    }
  };

  const streamCode = async (isRetry = false, errorMessage = null) => {
    try {
      setIsProcessing(true);
      setError(null);
      setOutput(null);
      setGeneratedJSX("");
      setActivePreview(activeTabValue.code);
      setShowPreview(false);
      setIsUsingTemplate(false);

      if (!isRetry) {
        retryCountRef.current = 0;
        setRetryCount(0);
      }

      let currentGeneratedJSX = "";
      if (followupCount.current === 0) {
        firstTimePrompt.current += inputCode;
      }

      const retryPrompt = isRetry
        ? `Previous attempt failed. Please fix any potential issues and ensure the code is valid React JSX. Common issues to check:
         1. Ensure all components are properly closed
         2. Check for proper state management
         3. Verify all required props are provided
         4. Ensure proper error handling
         5. No syntax errors in the JSX
         Original error: ${errorMessage}`
        : "";

      const currentSelections = window.currentGenerationParams;
      let effectiveGameType = currentSelections?.gameType;

      if (isEditMode && (!effectiveGameType || effectiveGameType === "none")) {
        effectiveGameType = GameType.SIMULATION;
      }

      const effectiveThemeColors = currentSelections?.themeColors;
      const effectiveThemeFonts = currentSelections?.themeFonts;
      const effectiveBackgroundImage =
        currentSelections?.backgroundImage || backgroundImage;

      const promptService = new PromptService(orgId, userId, "", "", "");
      const finalPrompt = await promptService.getFinalPrompt(
        selectedGameTypeRef.current && selectedGameTypeRef.current !== "none"
          ? selectedGameTypeRef.current
          : "simulation"
      );
      const selectedModel = await promptService.getModel();

      const modifiedSystemPrompt = getModifiedPrompt(
        finalPrompt,
        effectiveGameType,
        effectiveThemeColors,
        effectiveThemeFonts
      );
      console.log("modifiedSystemPrompt", modifiedSystemPrompt);
      const maxTokens =
        effectiveGameType === GameType.SIMULATION ? 20000 : 8192;

      const messages = [
        { role: "assistant", content: modifiedSystemPrompt },
        {
          role: "user",
          content:
            followupCount.current === 0
              ? `${
                  inputCode ? inputCode : firstTimePrompt.current
                } ${retryPrompt}`
              : `Modify the React JSX code based on the instructions and provide only JSX without any comments added. Make sure to not add any commentary before "const InteractiveElements = ()". The JSX output should start from "InteractiveElement" component. Do not make any other changes in the code beyond the user instructions. When providing code:
1. Provide complete, self-contained implementations with no external dependencies.
2. Include ALL questions array with complete question objects (never use placeholder comments like "// Same questions array...").
3. Write full implementations for all event handlers and callbacks.
4. Define all state variables, props, and interfaces/types.
5. Include all necessary styling and UI components. Implement all UI elements completely.
6. Never use TODO comments or placeholder code. Never use placeholder comments or partial implementation.
7. Never reference external or previous/existing code snippets.
8. Ensure all features are fully functional and testable.
9. At all times, ensure the code is production ready and complete.
10. Write complete logic for all interactions. 
11. Include ALL code explicitly and completely.
              ${retryPrompt}
              #########################
              JSX code- 
              ${prevGeneratedCodes.current}

              #########################
              Instruction - 
              ${inputCode}`,
        },
      ];

      await streamAnthropicMessages({
        messages,
        model: selectedModel,
        maxTokens,
        onText: (text) => {
          currentGeneratedJSX += text;
          setGeneratedJSX((prevCode) => prevCode + text);
        },
        onEnd: async () => {
          try {
            followupCount.current += 1;
            setFollowupCountState(followupCount.current);
            if (
              !currentGeneratedJSX.endsWith("}") ||
              !currentGeneratedJSX.endsWith("};")
            ) {
              currentGeneratedJSX += "}";
            }
            prevGeneratedCodes.current = currentGeneratedJSX;

            setTimeout(async () => {
              console.log("currentGeneratedJSX", { code: currentGeneratedJSX });
              await updatePreview(currentGeneratedJSX);
              setShowPreview(true);
              setActivePreview(activeTabValue.preview);
              setInputCode("");
              setIsProcessing(false);
              window.currentGenerationParams = null;
              setOriginalGeneratedCode(currentGeneratedJSX);
              // If automatic refine is enabled and we are in edit mode, add the game to the library
              if (automaticRefine && isEditMode) {
                handleAddNewGameToLibrary(false);
              }
            }, 500);
          } catch (previewError) {
            handleError(previewError);
          }
        },
        onError: (error) => {
          handleError(error);
        },
      });
    } catch (err) {
      handleError(err);
    }
  };

  // Main generation dispatcher
  const generateInteractive = async () => {
    const currentSelections = window.currentGenerationParams;
    let effectiveGameType =
      currentSelections?.gameType || selectedGameTypeRef.current;

    if (isEditMode && (!effectiveGameType || effectiveGameType === "none")) {
      effectiveGameType = GameType.SIMULATION;
    }

    if (effectiveGameType === GameType.SIMULATION) {
      await streamCode();
      return;
    }

    let shouldUseTemplates = false;
    if (orgId && userId) {
      try {
        shouldUseTemplates = await templateManager.checkTemplateAvailability(
          effectiveGameType,
          orgId,
          userId
        );
      } catch (error) {
        shouldUseTemplates = false;
      }
    }

    if (isEditMode) {
      if (shouldUseTemplates && window.currentQuestionData) {
        await generateTemplateBasedInteractive();
        return;
      } else {
        await streamCode();
        return;
      }
    }

    if (shouldUseTemplates) {
      await generateTemplateBasedInteractive();
    } else {
      await streamCode();
    }
  };

  const streamAndRunCode = async () => {
    if (inputCode.trim()) {
      const newInstruction = [
        {
          id: uuidv4(),
          instruction: inputCode,
        },
        ...userInstructionHistory,
      ].slice(0, MAX_INSTRUCTION_HISTORY);

      setUserInstructionHistory(newInstruction);
    }

    // Send the first instruction/topic to parent for storing as interactive title
    if (followupCount.current === 0 && inputCode.trim()) {
      window.parent.postMessage(
        {
          type: "storeInteractiveTitle",
          data: {
            title: inputCode.trim(),
          },
        },
        "*"
      );
    }

    if (!OPENAI_API_KEY) {
      setError("OpenAI API key is not set. Please set a valid API key.");
      return;
    }
    setShouldBeChargedGame(false);
    await generateInteractive();
  };

  const generateAndRunCode = async () => {
    setIsProcessing(true);
    setError(null);
    setOutput(null);

    if (!OPENAI_API_KEY) {
      setError("OpenAI API key is not set. Please set a valid API key.");
      setIsProcessing(false);
      return;
    }

    let modelToUse = "claude-3-5-sonnet-20240620"; // fallback
    if (orgId && userId) {
      try {
        const promptService = new PromptService(orgId, userId, "", "", "");
        await promptService.getSystemPrompt();
        modelToUse = await promptService.getModel();
      } catch (error) {
        console.warn("Failed to get model, using fallback");
      }
    }

    const aiModelRequest = {
      prompt:
        followupCount.current === 0
          ? inputCode
          : `Modify the given React JSX code based on given instructions and provide only JSX without any comments added.Also make sure to not add any commentry before "const InteractiveElements = ()". The JSX output should start from "InteractiveElement" component. Also make sure to make no other changes in the code beyond the user instructions.
      ##############
      JSX code- 
      ${prevGeneratedCodes.current}

      ##############
      Instruction - 
      ${inputCode}`,
      GPTResponseType: 1,
      systemPrompt: systemPrompt,
      requestType: 2,
      model: modelToUse,
      includeModerations: true,
    };

    try {
      const response = await axios.post(
        "https://testing-profjimapi.azurewebsites.net/v1/OpenAI/ChatGPTRequest",
        aiModelRequest,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (followupCount.current === 0) {
        firstTimePrompt.current = inputCode;
      }
      const stringifiedResponse = response.data.data.result;
      if (stringifiedResponse) {
        const parsedResponse = JSON.parse(stringifiedResponse);
        if (
          parsedResponse &&
          parsedResponse.content &&
          parsedResponse.content[0]
        ) {
          const generatedCode = parsedResponse.content[0].text;
          followupCount.current = followupCount.current + 1;
          setFollowupCountState(followupCount.current);
          setGeneratedJSX(generatedCode);

          try {
            updatePreview(generatedCode);
            setShowPreview(true);
            setInputCode("");
          } catch (evalError) {
            console.error("Error evaluating generated code:", evalError);
            setError(`Error in generated code: ${evalError.message}`);
          }
        } else {
          throw new Error("Unexpected response structure from API");
        }
      } else {
        throw new Error("Empty response from API");
      }
    } catch (err) {
      console.error("Error generating or running code:", err);
      if (axios.isAxiosError(err)) {
        if (err.response) {
          setError(
            `API Error: ${err.response.status} - ${
              err.response.data.error?.message || "Unknown error"
            }`
          );
        } else if (err.request) {
          setError(
            "No response received from the API. Please check your internet connection."
          );
        } else {
          setError(`Error setting up the request: ${err.message}`);
        }
      } else {
        setError(err.toString());
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const handleError = (error) => {
    console.error("Error in code generation:", error);

    const errorMessage = error instanceof Error ? error.message : String(error);

    // Use the ref for checking current retry count
    const nextRetryCount = retryCountRef.current + 1;
    retryCountRef.current = nextRetryCount;
    setRetryCount(nextRetryCount); // Update state for UI

    if (nextRetryCount >= MAX_RETRIES) {
      // setError(`Failed after ${MAX_RETRIES} attempts. Please try again or modify your prompt. `);
      setError(
        `Uh-oh! The AI couldn't generate your interactive. Let's try again.`
      );
      window.parent.postMessage(
        {
          type: "RetryExceeded",
        },
        "*"
      );
      setRetryCount(0);
      setIsProcessing(false);
      return;
    }

    // setError(`${error.message} (Attempt ${nextRetryCount} of ${MAX_RETRIES})`);
    setError(`Retrying...`);
    console.log(`Retrying... Attempt ${nextRetryCount} of ${MAX_RETRIES}`);

    // Actually perform the retry after a delay
    setTimeout(() => {
      streamCode(true, errorMessage);
    }, 2000);
  };

  const addToLibrary = () => {
    const gameId = uuidv4();
    const gameName = prompt("Enter a name for this exercise:");
    if (gameName) {
      const gameObject = {
        id: gameId,
        name: gameName,
        jsx: generatedJSX,
      };

      const existingGames = JSON.parse(localStorage.getItem("games") || "[]");
      existingGames.push(gameObject);
      localStorage.setItem("games", JSON.stringify(existingGames));

      alert(`Exercise "${gameName}" added to library!`);
    }
  };

  let buttonName = "Generate";
  if (followUpCountState > 0 && isProcessing) {
    buttonName = "Refining...";
  } else if (followUpCountState > 0 && !isProcessing) {
    buttonName = "Refine";
  } else if (followUpCountState === 0 && isProcessing) {
    buttonName = "Generating...";
  } else {
    buttonName = "Generate";
  }

  const renderGamePreview = () => {
    try {
      return (
        <>
          <div
            id="game-container"
            ref={gameContainerRef}
            style={{
              display: activePreview === activeTabValue.preview ? "" : "none",
            }}
          ></div>
        </>
      );
    } catch (err) {
      console.log("Error occured ");
      return (
        <>
          <div>Error occured: {err}</div>
        </>
      );
    }
  };

  useEffect(() => {
    const handleParentMessage = (event) => {
      if (event.data.type === "updateCode") {
        const { code } = event.data;
        setGeneratedJSX(code);
        updatePreview(code);
        setShowPreview(true);
        setActivePreview(activeTabValue.preview);
      }
    };

    window.addEventListener("message", handleParentMessage);
    return () => window.removeEventListener("message", handleParentMessage);
  }, []);

  if (!isAuthenticated) {
    return (
      <>
        <div
          style={{
            height: "100vh",
            width: "100vw",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#f5f5f5",
          }}
        >
          <Lock
            sx={{
              fontSize: 64,
              color: profJimBrandColors.purple,
              marginBottom: 2,
            }}
          />
          <Typography
            variant="h4"
            sx={{
              color: profJimBrandColors.purple,
              fontWeight: "bold",
              textAlign: "center",
              fontFamily: themeColor.primaryFont,
            }}
          >
            Access Denied
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "#6f6e6e",
              marginTop: 2,
              textAlign: "center",
              fontFamily: themeColor.primaryFont,
            }}
          >
            You are not authorized to use this feature.
          </Typography>
        </div>
      </>
    );
  }

  if (!cssLoaded) {
    return (
      <Stack
        direction="row"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress size={60} sx={{ color: profJimBrandColors.orange }} />
      </Stack>
    );
  }

  return (
    <>
      <div
        style={{
          fontFamily: "Poppins",
          padding: codeFromUrl ? "0px" : "20px",
        }}
      >
        <Grid container spacing={4}>
          <Grid
            item
            md={12}
            xl={4}
            lg={4}
            sm={12}
            display={codeFromUrl ? "none" : ""}
          >
            <div>
              <Typography
                variant="h5"
                sx={{
                  color: themeColor.headerColor,
                  fontWeight: "bold",
                  fontSize: "18px",
                  fontFamily: themeColor.primaryFont,
                }}
              >
                {instructions
                  ? instructions
                  : clientType === promptEngineeringPlaygroundClientId.boston
                  ? ""
                  : "Transform your idea into interactive elements"}
              </Typography>
              <ModeSelector />
              <Typography
                variant="body2"
                sx={{
                  color: themeColor.instructionColor,
                  marginTop: "5px",
                  fontSize: "14px",
                  fontFamily: "Poppins",
                  marginBottom: "20px",
                }}
              >
                Enter your desired topic in the provided field, then click the
                "Generate" button.
              </Typography>

              <GameTypeSelector />

              <TextField
                value={inputCode}
                onChange={handleInputChange}
                placeholder={getPlaceholderText()}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: profJimBrandColors.white,
                  },
                  "& .MuiInputBase-input": {
                    color: profJimBrandColors.grey,
                    fontFamily: themeColor.primaryFont,
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: profJimBrandColors.purple + " !important",
                  },
                  "& .MuiInputLabel-formControl": {
                    color: profJimBrandColors.purple,
                    fontFamily: themeColor.primaryFont,
                  },
                  "& .Mui-focused": {
                    color: profJimBrandColors.purple + " !important",
                  },
                  "& .MuiInputLabel-outlined.MuiInputLabel-shrink": {
                    background: profJimBrandColors.white + " !important",
                    fontFamily: themeColor.primaryFont,
                  },
                  "& .MuiAutocomplete-popupIndicator": {
                    display: "none",
                  },
                  width: "100%",
                  fontFamily: themeColor.primaryFont,
                }}
                fullWidth
                multiline
                minRows={2}
                variant="outlined"
              ></TextField>
              {/* {mode === GenerationMode.fast && clientType !== promptEngineeringPlaygroundClientId.boston &&
                clientType !==
                  promptEngineeringPlaygroundClientId.k12Coalition && (
                  <div
                    style={{
                      marginTop: "4px",
                      display: "flex",
                      justifyContent: "right",
                      fontSize: "14px",
                    }}
                  >
                    <span
                      onClick={(e) => {
                        setOpenEditPromptPopover(true);
                        setEditPromptAnchorEl(e.currentTarget);
                      }}
                      style={{
                        color: themeColor.headerColor,
                        cursor: "pointer",
                      }}
                    >
                      Show prompt?
                    </span>
                  </div>
                )} */}

              {openEditPromptPopover && (
                <MuiPopover
                  open={openEditPromptPopover}
                  onClose={() => {
                    setOpenEditPromptPopover(false);
                    setEditPromptAnchorEl(null);
                  }}
                  anchorEl={editPromptAnchorEl}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "center",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "center",
                  }}
                  sx={{
                    ".MuiPopover-paper": {
                      marginTop: "5px",
                      backgroundColor: profJimBrandColors.white,
                      borderRadius: "18px",
                    },
                  }}
                >
                  <div style={{ minWidth: "650px", padding: "10px" }}>
                    <TextField
                      value={systemPrompt}
                      onChange={(e) => {
                        setSystemPrompt(e.target.value);
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: profJimBrandColors.white,
                        },
                        "& .MuiInputBase-input": {
                          color: profJimBrandColors.grey,
                          fontFamily: themeColor.primaryFont + " !important",
                        },
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor:
                            profJimBrandColors.purple + " !important",
                        },
                        "& .MuiInputLabel-formControl": {
                          color: profJimBrandColors.purple,
                          fontFamily: themeColor.primaryFont + " !important",
                        },

                        "& .Mui-focused": {
                          color: profJimBrandColors.purple + " !important",
                        },
                        "& .MuiInputLabel-outlined.MuiInputLabel-shrink": {
                          background: profJimBrandColors.white + " !important",
                          fontFamily: themeColor.primaryFont + " !important",
                        },
                        "& .MuiAutocomplete-popupIndicator": {
                          display: "none",
                        },
                        width: "100%",
                        maxHeight: "400px",
                        overflow: "auto",
                        fontFamily: themeColor.primaryFont,
                      }}
                      fullWidth
                      multiline
                      minRows={20}
                      variant="outlined"
                    />
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        marginTop: "20px",
                        gap: "20px",
                      }}
                    >
                      <Button
                        variant="contained"
                        onClick={() => {
                          setSystemPrompt(systemPromptToGenerateInteractives);
                        }}
                        sx={{
                          backgroundColor: profJimBrandColors.purple,
                          fontFamily: themeColor.primaryFont,
                          textTransform: "none",
                        }}
                      >
                        Restore default prompt
                      </Button>
                      <Button
                        variant="contained"
                        onClick={() => {
                          setOpenEditPromptPopover(false);
                          setEditPromptAnchorEl(false);
                        }}
                        sx={{
                          backgroundColor: profJimBrandColors.purple,
                          fontFamily: themeColor.primaryFont,
                          textTransform: "none",
                        }}
                      >
                        Done
                      </Button>
                    </div>
                  </div>
                </MuiPopover>
              )}

              <Button
                sx={{
                  backgroundColor: themeColor.buttonColor,
                  "&:hover": {
                    backgroundColor: themeColor.buttonColor,
                  },
                  marginTop: "18px",
                  borderRadius: "18px",
                  textTransform: "none !important",
                  fontFamily: themeColor.primaryFont,
                }}
                variant="contained"
                onClick={() => {
                  const currentSelections = {
                    gameType: selectedGameType,
                    themeColors,
                    themeFonts,
                    backgroundImage,
                  };
                  window.currentGenerationParams = currentSelections;

                  if (buttonName === "Refine") {
                    if (shouldBeChargedGame) {
                      window.parent.postMessage(
                        {
                          type: "CheckForCredits",
                        },
                        "*"
                      );
                    } else {
                      streamAndRunCode();
                    }
                  } else {
                    handleGenerateCode();
                  }
                }}
                disabled={
                  isProcessing ||
                  (!inputCode &&
                    !isProcessing &&
                    (selectedGameType === GameType.SIMULATION ||
                      followUpCountState === 0)) ||
                  !selectedGameType
                }
                startIcon={
                  isProcessing ? (
                    <CircularProgress
                      style={{ width: "25px", height: "25px" }}
                    />
                  ) : (
                    <></>
                  )
                }
              >
                {buttonName}
              </Button>

              {/* Re-generate button is removed as it is not needed */}
              {/* {followUpCountState > 0 && !disableRegenerate && (
                <Button
                  sx={{
                    backgroundColor: themeColor.buttonColor,
                    "&:hover": {
                      backgroundColor: themeColor.buttonColor,
                    },
                    marginTop: "18px",
                    borderRadius: "18px",
                    marginLeft: "14px",
                    textTransform: "none !important",
                    fontFamily: themeColor.primaryFont,
                  }}
                  variant="contained"
                  onClick={() => {
                    followupCount.current = 0;
                    setFollowupCountState(0);
                    setRetryCount(0);
                    handleGenerateCode();
                  }}
                  disabled={isProcessing}
                >
                  Re-generate
                </Button>
              )} */}

              {followUpCountState > 0 && !disableNewSession && (
                <Button
                  sx={{
                    backgroundColor: themeColor.buttonColor,
                    "&:hover": {
                      backgroundColor: themeColor.buttonColor,
                    },
                    marginTop: "18px",
                    marginLeft: "14px",
                    borderRadius: "18px",
                    textTransform: "none !important",
                    fontFamily: themeColor.primaryFont,
                  }}
                  variant="contained"
                  onClick={() => {
                    followupCount.current = 0;
                    setFollowupCountState(0);
                    setGeneratedJSX("");
                    setInputCode("");
                    setIsProcessing(false);
                    setError(null);
                    setShowPreview(false);
                    setAddedLibraryUrl("");
                    setIsFixingCode(false);
                    setIsAddingToLibrary(false);
                    setActivePreview(activeTabValue.preview);
                    setGeneratedComponent(null);
                    prevGeneratedCodes.current = "";
                    firstTimePrompt.current = "";
                    setCurrentStage(1);
                    setStageOutputs({
                      learningOutcomes: null,
                      reactCode: null,
                      finalCode: null,
                    });
                    setSelectedGameType(null);
                    selectedGameTypeRef.current = null;
                    setSystemPrompt(systemPromptToGenerateInteractives);
                    setUserInstructionHistory([]);
                    setEnableCodeValidation(true);
                    setOriginalGeneratedCode("");
                    setShowPreviewUpdateSuccess(false);
                    setIsCurrentCodePreviewed(true);

                    // Clear template data
                    window.currentTemplateData = null;
                    window.currentQuestionData = null;
                    window.currentGenerationParams = null;

                    // Abort any ongoing requests
                    if (abortControllerRef.current) {
                      abortControllerRef.current.abort();
                      abortControllerRef.current = null;
                    }

                    if (rootRef.current) {
                      rootRef.current.unmount();
                      rootRef.current = null;
                    }
                  }}
                  startIcon={<Add />}
                >
                  Start new session
                </Button>
              )}
              <div style={{ marginTop: "12px" }}>
                {isProcessing && (
                  <Typography
                    variant="body2"
                    sx={{
                      color: themeColor.instructionColor,
                      fontFamily: themeColor.primaryFont,
                      fontSize: "14px",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <LucideIcons.Clock className="animate-spin" />
                    {followUpCountState > 0
                      ? (() => {
                          const currentSelections =
                            window.currentGenerationParams;
                          let effectiveGameType =
                            currentSelections?.gameType ||
                            selectedGameTypeRef.current;

                          if (
                            isEditMode &&
                            (!effectiveGameType || effectiveGameType === "none")
                          ) {
                            effectiveGameType = GameType.SIMULATION;
                          }

                          if (effectiveGameType === GameType.SIMULATION) {
                            return "Please wait while we refine your interactive...";
                          } else {
                            return "Please wait while we refine your interactive content...";
                          }
                        })()
                      : mode === GenerationMode.quality
                      ? `${getStageDescription(currentStage)}`
                      : (() => {
                          const currentSelections =
                            window.currentGenerationParams;
                          let effectiveGameType =
                            currentSelections?.gameType ||
                            selectedGameTypeRef.current;

                          if (
                            isEditMode &&
                            (!effectiveGameType || effectiveGameType === "none")
                          ) {
                            effectiveGameType = GameType.SIMULATION;
                          }

                          if (effectiveGameType === GameType.SIMULATION) {
                            return "We are generating your interactive at a rate of ~200 lines of code per minute...";
                          } else {
                            return isUsingTemplate
                              ? "Generating using optimized templates for super-fast creation..."
                              : "We are generating your interactive at a rate of ~200 lines of code per minute...";
                          }
                        })()}
                  </Typography>
                )}
              </div>
              {userInstructionHistoryTracker}
            </div>
          </Grid>
          <Grid item md={12} xl={8} lg={8} sm={12}>
            <div style={{ width: "100%", marginTop: "0px" }}>
              <CodePreviewPanel
                error={error}
                retryCountRef={retryCountRef}
                MAX_RETRIES={MAX_RETRIES}
                addedLibraryUrl={addedLibraryUrl}
                themeColor={themeColor}
                profJimBrandColors={profJimBrandColors}
                activePreview={activePreview}
                setActivePreview={setActivePreview}
                activeTabValue={activeTabValue}
                generatedJSX={generatedJSX}
                isEmbed={isEmbed}
                isAddingToLibrary={isAddingToLibrary}
                handleAddNewGameToLibrary={handleAddNewGameToLibrary}
                handleGeneratedCodeChange={handleGeneratedCodeChange}
                isProcessing={isProcessing}
                renderGamePreview={renderGamePreview}
                codeFromUrl={codeFromUrl}
                isEditMode={isEditMode}
                enableCodeValidation={enableCodeValidation}
                onManualPreviewUpdate={() => updatePreview(generatedJSX, true)}
                hasCodeBeenEdited={
                  generatedJSX !== originalGeneratedCode &&
                  originalGeneratedCode !== ""
                }
                showPreviewUpdateSuccess={showPreviewUpdateSuccess}
                isCurrentCodePreviewed={isCurrentCodePreviewed}
              />

              {/* {error && (
                <Button
                  sx={{
                    backgroundColor: profJimBrandColors.purple,
                    textTransform: "none",
                    marginTop: "20px",
                    marginRight: "20px",
                    borderRadius: "18px",
                  }}
                  variant="contained"
                  disabled={isFixingCode}
                  onClick={() => {
                    fixPastedOrIncorrectCodeUsingGPT(generatedJSX);
                  }}
                >
                  {isFixingCode ? "Working on it..." : "Fix code"}
                </Button>
              )}*/}
            </div>
            <br />
          </Grid>
        </Grid>
      </div>
    </>
  );
};

export default CodeEditorAndRunner;
